// ============================================================
// HARMONIC CHARTER — Collaboration Store (Zustand)
// ============================================================
// Manages real-time collaboration state for "linked write" mode.
// Tracks who is present in the document and which bar they are
// currently editing.
// ============================================================

import { create } from 'zustand';

// ------------------------------------------------------------
// TYPES
// ------------------------------------------------------------

export interface Presence {
  userId:    string;
  email:     string;
  color:     string;   // assigned deterministically from userId
  barId:     string | null;  // which bar they're currently editing
  lastSeen:  number;   // Date.now() timestamp
}

export interface CollaborationStore {
  // Current chart's collaboration mode
  mode:        'none' | 'view' | 'write' | 'linked';
  songId:      string | null;

  // Presence map: userId → Presence
  presence:    Record<string, Presence>;

  // Actions
  setMode:     (mode: CollaborationStore['mode'], songId: string | null) => void;
  upsertPresence: (presence: Presence) => void;
  removePresence: (userId: string) => void;
  clearPresence:  () => void;
  setLocalBarFocus: (barId: string | null) => void;
}

// ------------------------------------------------------------
// PRESENCE COLORS
// Deterministic color from userId so each collaborator always
// gets the same color across sessions.
// ------------------------------------------------------------

const PRESENCE_COLORS = [
  '#e63946', // red
  '#2a9d8f', // teal
  '#e9c46a', // yellow
  '#f4a261', // orange
  '#457b9d', // blue
  '#8338ec', // purple
  '#06d6a0', // green
  '#fb5607', // deep orange
];

export function presenceColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length];
}

// ------------------------------------------------------------
// STORE
// ------------------------------------------------------------

export const useCollaborationStore = create<CollaborationStore>((set, get) => ({
  mode:     'none',
  songId:   null,
  presence: {},

  setMode: (mode, songId) => set({ mode, songId, presence: {} }),

  upsertPresence: (presence) => set((state) => ({
    presence: { ...state.presence, [presence.userId]: presence },
  })),

  removePresence: (userId) => set((state) => {
    const next = { ...state.presence };
    delete next[userId];
    return { presence: next };
  }),

  clearPresence: () => set({ presence: {} }),

  setLocalBarFocus: (barId) => {
    // Called when local user focuses a bar — stored separately so we can
    // broadcast it without including it in the presence map (which is for
    // remote users only).
    // The actual broadcast is handled in App.tsx via broadcastPresence().
    void barId;
  },
}));
